import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const FeaturesSection = () => {
  const features = [
  {
    id: 1,
    icon: 'Grid3x3',
    title: 'Smart Business Model Canvas',
    description: 'Interactive 9-block canvas with AI-powered suggestions for each component. Build and refine your business model with intelligent recommendations.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12ca8f8f2-1766749339258.png",
    imageAlt: 'Business professional working on strategic planning documents with colorful sticky notes and diagrams spread across modern office desk',
    highlights: ['9 Editable Blocks', 'AI Suggestions', 'Strategy Reports', 'PDF Export']
  },
  {
    id: 2,
    icon: 'TrendingUp',
    title: 'Predictive Growth Engine',
    description: 'Advanced financial forecasting with service and product modes. Upload Excel data or enter manually to get 1-2 year projections with risk indicators.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3895ecc-1764656426788.png",
    imageAlt: 'Financial analyst reviewing growth charts and revenue projections on laptop screen with upward trending graphs and data analytics dashboard',
    highlights: ['Revenue Forecasting', 'Seasonal Analysis', 'Risk Assessment', 'Excel Integration']
  },
  {
    id: 3,
    icon: 'Target',
    title: 'Goal Intelligence Planner',
    description: 'Set ambitious goals with AI-driven feasibility analysis. Get quarterly milestones, gap analysis, and actionable steps to achieve your targets.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1efc8391b-1767700962291.png",
    imageAlt: 'Diverse business team collaborating around conference table with laptops and documents discussing strategic goals and objectives',
    highlights: ['Feasibility Scoring', 'Milestone Tracking', 'Gap Analysis', 'Risk Alerts']
  }];


  return (
    <section id="features-section" className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-4">
            <Icon name="Sparkles" size={16} color="var(--color-primary)" />
            <span className="text-xs md:text-sm font-medium text-primary font-caption">
              Core Features
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Everything You Need to Succeed
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Three powerful modules working together to provide comprehensive business intelligence and decision support.
          </p>
        </div>

        <div className="space-y-12 md:space-y-16 lg:space-y-20">
          {features?.map((feature, index) =>
          <div
            key={feature?.id}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center ${
            index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`
            }>
            
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full mb-4">
                  <Icon name={feature?.icon} size={16} color="var(--color-primary)" />
                  <span className="text-xs font-medium text-primary font-caption">
                    Feature {feature?.id}
                  </span>
                </div>
                <h3 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground mb-4">
                  {feature?.title}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground font-body mb-6 leading-relaxed">
                  {feature?.description}
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {feature?.highlights?.map((highlight, idx) =>
                <div key={idx} className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                        <Icon name="Check" size={12} color="var(--color-success)" strokeWidth={3} />
                      </div>
                      <span className="text-xs md:text-sm text-foreground font-body">{highlight}</span>
                    </div>
                )}
                </div>
              </div>

              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="relative rounded-xl overflow-hidden elevation-lg group">
                  <Image
                  src={feature?.image}
                  alt={feature?.imageAlt}
                  className="w-full h-auto aspect-[4/3] object-cover transition-smooth group-hover:scale-105" />
                
                  <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent opacity-0 group-hover:opacity-100 transition-smooth" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default FeaturesSection;